import React, { useState, useEffect } from 'react';
import { useBiometric } from '@/context/BiometricContext';
import { BiometricCredential } from '../types/biometric';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Switch } from '@vaiu/ui/switch';
import { Label } from '@vaiu/ui/label';
import { Input } from '@vaiu/ui/input';
import { Badge } from '@vaiu/ui/badge';
import { Separator } from '@vaiu/ui/separator';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@vaiu/ui/dialog';
import {
  Fingerprint,
  Smartphone,
  Shield,
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  CheckCircle,
  Info,
  Key,
  Settings,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { i18n } from '@/utils/i18n';

interface BiometricSettingsProps {
  className?: string;
}

const BiometricSettings: React.FC<BiometricSettingsProps> = ({ className }) => {
  const {
    support,
    isLoading,
    settings,
    credentials,
    registerCredential,
    deleteCredential,
    updateCredentialNickname,
    updateSettings,
    refreshSettings,
    biometricError,
    setBiometricError
  } = useBiometric();

  const [showAddCredentialForm, setShowAddCredentialForm] = useState(false);
  const [showEditCredentialDialog, setShowEditCredentialDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [newCredentialNickname, setNewCredentialNickname] = useState('');
  const [editingCredential, setEditingCredential] = useState<BiometricCredential | null>(null);
  const [editCredentialNickname, setEditCredentialNickname] = useState('');
  const [deletingCredential, setDeletingCredential] = useState<BiometricCredential | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    if (!support?.isSupported) {
      return;
    }
    let cancelled = false;
    (async () => {
      await refreshSettings();
      if (!cancelled) {
        setSettingsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [support?.isSupported, refreshSettings]);

  // Clear error when inline add form or dialogs close
  useEffect(() => {
    if (!showAddCredentialForm && !showEditCredentialDialog && !showDeleteConfirmDialog) {
      setBiometricError(null);
    }
  }, [showAddCredentialForm, showEditCredentialDialog, showDeleteConfirmDialog, setBiometricError]);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Biometric Authentication
          </CardTitle>
          <CardDescription>
            Loading biometric authentication settings...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!support?.isSupported) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Biometric Authentication
          </CardTitle>
          <CardDescription>
            Secure your account with fingerprint or face recognition
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {support?.error || 'Biometric authentication is not supported on this device or browser.'}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!settingsLoaded) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Biometric Authentication
          </CardTitle>
          <CardDescription>Loading biometric authentication settings...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!support.isAvailable) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Biometric Authentication
          </CardTitle>
          <CardDescription>
            Secure your account with fingerprint or face recognition
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Biometric authentication is supported but not available. Please set up a fingerprint or face recognition on your device first.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const handleToggleEnabled = async (enabled: boolean) => {
    try {
      setIsUpdating(true);
      await updateSettings({ enabled });
      toast.success(enabled ? 'Biometric authentication enabled' : 'Biometric authentication disabled');
    } catch (error) {
      toast.error('Failed to update biometric settings');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleRequireForLogin = async (requireForLogin: boolean) => {
    try {
      setIsUpdating(true);
      await updateSettings({ requireForLogin });
      toast.success(requireForLogin ? 'Biometric login required' : 'Biometric login optional');
    } catch (error) {
      toast.error('Failed to update biometric settings');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleRequireForTransactions = async (requireForTransactions: boolean) => {
    try {
      setIsUpdating(true);
      await updateSettings({ requireForTransactions });
      toast.success(requireForTransactions ? 'Biometric verification required for transactions' : 'Biometric verification optional for transactions');
    } catch (error) {
      toast.error('Failed to update biometric settings');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddCredential = async () => {
    try {
      setIsRegistering(true);
      setBiometricError(null);
      
      const credential = await registerCredential(newCredentialNickname.trim() || undefined);
      
      setShowAddCredentialForm(false);
      setNewCredentialNickname('');
      toast.success('Biometric credential added successfully');
      
      // Refresh settings to get updated credentials
      await refreshSettings();
    } catch (error) {
      console.error('Failed to add biometric credential:', error);
      // Error is handled by the context
    } finally {
      setIsRegistering(false);
    }
  };

  const handleEditCredential = async () => {
    if (!editingCredential) return;
    
    try {
      setIsUpdating(true);
      setBiometricError(null);
      
      await updateCredentialNickname(editingCredential.id, editCredentialNickname.trim());
      
      setShowEditCredentialDialog(false);
      setEditingCredential(null);
      setEditCredentialNickname('');
      toast.success('Credential nickname updated');
      
      // Refresh settings to get updated credentials
      await refreshSettings();
    } catch (error) {
      console.error('Failed to update credential nickname:', error);
      // Error is handled by the context
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteCredential = async () => {
    if (!deletingCredential) return;
    
    try {
      setIsDeleting(true);
      setBiometricError(null);
      
      await deleteCredential(deletingCredential.id);
      
      setShowDeleteConfirmDialog(false);
      setDeletingCredential(null);
      toast.success('Biometric credential removed');
      
      // Refresh settings to get updated credentials
      await refreshSettings();
    } catch (error) {
      console.error('Failed to delete credential:', error);
      // Error is handled by the context
    } finally {
      setIsDeleting(false);
    }
  };

  const openEditDialog = (credential: BiometricCredential) => {
    setEditingCredential(credential);
    setEditCredentialNickname(credential.nickname || '');
    setShowEditCredentialDialog(true);
  };

  const openDeleteDialog = (credential: BiometricCredential) => {
    setDeletingCredential(credential);
    setShowDeleteConfirmDialog(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDeviceIcon = (deviceType: string | undefined) => {
    if (!deviceType) {
      return <Fingerprint className="h-4 w-4" />;
    }
    
    switch (deviceType.toLowerCase()) {
      case 'platform':
        return <Smartphone className="h-4 w-4" />;
      case 'cross-platform':
        return <Key className="h-4 w-4" />;
      default:
        return <Fingerprint className="h-4 w-4" />;
    }
  };

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Biometric Authentication
          </CardTitle>
          <CardDescription>
            Secure your account with fingerprint, face recognition, or other biometric methods
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Biometric Error Display */}
          {biometricError && !showAddCredentialForm && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{biometricError}</AlertDescription>
            </Alert>
          )}

          {/* Support Information */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm">Biometric authentication is supported</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Supported methods: {support.supportedAuthenticators.join(', ')}
              </span>
            </div>
          </div>

          <Separator />

          {/* Settings */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Biometric Authentication</Label>
                <p className="text-sm text-muted-foreground">
                  Allow using biometric authentication for your account
                </p>
              </div>
              <Switch
                checked={settings?.enabled || false}
                onCheckedChange={handleToggleEnabled}
                disabled={isUpdating}
              />
            </div>

            {settings?.enabled && (
              <>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Require for Login</Label>
                    <p className="text-sm text-muted-foreground">
                      Require biometric authentication when logging in
                    </p>
                  </div>
                  <Switch
                    checked={settings?.requireForLogin || false}
                    onCheckedChange={handleToggleRequireForLogin}
                    disabled={isUpdating}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Require for Transactions</Label>
                    <p className="text-sm text-muted-foreground">
                      Require biometric verification for sensitive transactions
                    </p>
                  </div>
                  <Switch
                    checked={settings?.requireForTransactions || false}
                    onCheckedChange={handleToggleRequireForTransactions}
                    disabled={isUpdating}
                  />
                </div>
              </>
            )}
          </div>

          {settings?.enabled && (
            <>
              <Separator />

              {/* Credentials Management */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Registered Devices</h4>
                    <p className="text-sm text-muted-foreground">
                      Manage your biometric credentials
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      if (showAddCredentialForm) {
                        setShowAddCredentialForm(false);
                        setNewCredentialNickname('');
                        setBiometricError(null);
                      } else {
                        setShowAddCredentialForm(true);
                      }
                    }}
                    size="sm"
                    variant={showAddCredentialForm ? 'outline' : 'default'}
                  >
                    {showAddCredentialForm ? (
                      <>
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Device
                      </>
                    )}
                  </Button>
                </div>

                {showAddCredentialForm && (
                  <div className="p-4 border-2 border-dashed border-primary/20 rounded-lg bg-muted/20 space-y-4">
                    <div className="flex items-center gap-2">
                      <Fingerprint className="h-4 w-4 text-primary" />
                      <h4 className="font-medium text-primary">Add Biometric Device</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Register a new biometric credential for this device. You&apos;ll be prompted to use your fingerprint, face recognition, or other biometric method.
                    </p>

                    {biometricError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{biometricError}</AlertDescription>
                      </Alert>
                    )}

                    <div>
                      <Label htmlFor="credential-nickname">Device Nickname (Optional)</Label>
                      <Input
                        id="credential-nickname"
                        placeholder="e.g., My iPhone, Work Laptop"
                        value={newCredentialNickname}
                        onChange={(e) => setNewCredentialNickname(e.target.value)}
                        disabled={isRegistering}
                        className="mt-1"
                      />
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={handleAddCredential}
                        disabled={isRegistering}
                        size="sm"
                      >
                        {isRegistering ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Registering...
                          </>
                        ) : (
                          <>
                            <Fingerprint className="h-4 w-4 mr-2" />
                            Register Device
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowAddCredentialForm(false);
                          setNewCredentialNickname('');
                          setBiometricError(null);
                        }}
                        disabled={isRegistering}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {credentials.length === 0 ? (
                  <div className="text-center py-8">
                    <Fingerprint className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">
                      No biometric credentials registered
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Add a device to start using biometric authentication
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {credentials.map((credential) => (
                      <div
                        key={credential.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {getDeviceIcon(credential.deviceType)}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {credential.nickname || 'Unnamed Device'}
                              </span>
                              <Badge variant="secondary" className="text-xs">
                                {credential.deviceType || 'Security Key'}
                              </Badge>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Added {formatDate(credential.createdAt)}
                              {credential.lastUsedAt && (
                                <span> • Last used {formatDate(credential.lastUsedAt)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(credential)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(credential)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Credential Dialog */}
      <Dialog open={showEditCredentialDialog} onOpenChange={setShowEditCredentialDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Device Nickname</DialogTitle>
            <DialogDescription>
              Update the nickname for this biometric credential.
            </DialogDescription>
          </DialogHeader>
          
          {biometricError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{biometricError}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-credential-nickname">Device Nickname</Label>
              <Input
                id="edit-credential-nickname"
                placeholder="e.g., My iPhone, Work Laptop"
                value={editCredentialNickname}
                onChange={(e) => setEditCredentialNickname(e.target.value)}
                disabled={isUpdating}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditCredentialDialog(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditCredential}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                'Update'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Biometric Device</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this biometric credential? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {biometricError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{biometricError}</AlertDescription>
            </Alert>
          )}
          
          {deletingCredential && (
            <div className="p-4 border rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                {getDeviceIcon(deletingCredential.deviceType)}
                <div>
                  <div className="font-medium">
                    {deletingCredential.nickname || 'Unnamed Device'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Added {formatDate(deletingCredential.createdAt)}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirmDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCredential}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove Device
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BiometricSettings; 